import { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ShoppingBag, Plus, Minus, Truck, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { startRazorpayCheckout } from '@/lib/razorpay';
import { startCashfreeCheckout, preloadCashfree } from '@/lib/cashfree';

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const LOGO = '/indivalife-logo.png';

export default function PayLink() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [lines, setLines] = useState([]);
  const [quote, setQuote] = useState(null);
  const [info, setInfo] = useState({ full_name: '', phone: '', address: '' });
  const [provider, setProvider] = useState('razorpay');
  const [paying, setPaying] = useState(false);

  useEffect(() => { preloadCashfree(); }, []);

  const loadLink = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/payment-links/${token}`);
      setLink(data);
      setLines((data.items || []).map(l => ({ product_id: l.product_id, title: l.title, price: l.price, image: l.image, size: l.size, colour: l.colour, qty: l.qty })));
      setQuote(data.quote);
    } catch { setNotFound(true); }
    finally { setLoading(false); }
  }, [token]);
  useEffect(() => { loadLink(); }, [loadLink]);

  const refreshQuote = useCallback(async () => {
    if (!link || lines.length === 0) return;
    try {
      const items = lines.map(l => ({ product_id: l.product_id, qty: l.qty, size: l.size, colour: l.colour }));
      const { data } = await api.post('/store/quote', { items, ref: link.ref });
      setQuote(data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not recalculate total'); }
  }, [lines, link]);
  useEffect(() => { refreshQuote(); }, [refreshQuote]);

  // Finish a Cashfree payment after the hosted-checkout redirect returns.
  useEffect(() => {
    const cfOrder = params.get('cf_verify');
    if (!cfOrder) return;
    (async () => {
      try {
        const { data } = await api.post('/store/cashfree-verify', { order_id: cfOrder });
        if (data?.status === 'paid') toast.success('Payment successful — order placed! 🎉');
        else toast('Payment is still processing — we could not confirm it yet.');
      } catch (e) { toast.error(e?.response?.data?.detail || 'Could not confirm the payment'); }
      finally {
        const url = new URL(window.location.href);
        url.searchParams.delete('cf_verify');
        window.history.replaceState({}, '', url.pathname);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setQty = (idx, d) => setLines(prev => prev.map((l, i) => i === idx ? { ...l, qty: Math.max(1, l.qty + d) } : l));
  const cartCount = lines.reduce((s, l) => s + l.qty, 0);
  const minItems = link?.min_items || 4;

  const pay = async () => {
    if (cartCount < minItems) { toast.error(`Minimum ${minItems} items — add ${minItems - cartCount} more`); return; }
    if (!info.full_name || !info.phone || !info.address) { toast.error('Please fill your name, phone and delivery address'); return; }
    setPaying(true);
    try {
      const items = lines.map(l => ({ product_id: l.product_id, qty: l.qty, size: l.size, colour: l.colour }));
      const orderPayload = { items, ref: link.ref, ...info };
      if (provider === 'cashfree') {
        await startCashfreeCheckout({ createPath: '/store/checkout', orderPayload, returnPath: `/pay/${token}` });
        return;
      }
      await startRazorpayCheckout({
        createPath: '/store/checkout', verifyPath: '/store/verify',
        orderPayload, phone: info.phone,
        onSuccess: () => toast.success('Payment successful — order placed! 🎉'),
      });
    } catch (e) { toast.error(e.message || 'Payment failed'); }
    finally { setPaying(false); }
  };

  if (loading) return <div className="min-h-screen grid place-items-center text-slate-500">Loading payment link…</div>;
  if (notFound) return (
    <div className="min-h-screen grid place-items-center p-6 text-center">
      <Card className="p-8 max-w-sm" data-testid="paylink-not-found">
        <div className="text-lg font-bold text-slate-800">Link not available</div>
        <p className="text-sm text-slate-500 mt-2">This payment link has expired or was removed.</p>
      </Card>
    </div>
  );

  return (
    <main className="min-h-screen bg-slate-50 p-4 sm:p-6" data-testid="paylink-page">
      <div className="max-w-lg mx-auto">
        <div className="flex items-center gap-2 mb-4">
          <img src={LOGO} alt="INDIVALIFE" className="h-9 w-9 rounded-xl" />
          <div>
            <div className="font-black display text-[#1E3A8A] leading-tight">INDIVALIFE Store</div>
            <div className="text-[11px] text-slate-500">Shared by {link.creator_name}</div>
          </div>
        </div>

        <Card className="p-4 rounded-2xl">
          <div className="text-sm font-bold text-slate-700 mb-2 flex items-center gap-1.5"><ShoppingBag className="h-4 w-4" />Your order</div>
          <div className="space-y-3">
            {lines.map((l, idx) => (
              <div key={idx} className="flex items-center gap-3 border-b pb-2" data-testid={`paylink-line-${l.product_id}`}>
                <img src={l.image || 'https://placehold.co/80'} alt="" className="h-14 w-14 rounded object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{l.title}</div>
                  <div className="text-[11px] text-slate-500">{[l.size, l.colour].filter(Boolean).join(' · ')} {money(l.price)}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <Button size="icon" variant="outline" className="h-6 w-6" onClick={() => setQty(idx, -1)} data-testid={`paylink-dec-${l.product_id}`}><Minus className="h-3 w-3" /></Button>
                    <span className="text-sm font-bold w-5 text-center" data-testid={`paylink-qty-${l.product_id}`}>{l.qty}</span>
                    <Button size="icon" variant="outline" className="h-6 w-6" onClick={() => setQty(idx, 1)} data-testid={`paylink-inc-${l.product_id}`}><Plus className="h-3 w-3" /></Button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {quote && (
            <div className="bg-[var(--bz-soft)] rounded-xl p-3 text-sm space-y-1 mt-3" data-testid="paylink-quote">
              <div className="flex justify-between"><span>Subtotal</span><span>{money(quote.subtotal)}</span></div>
              <div className="flex justify-between"><span>GST ({quote.gst_pct}%)</span><span>{money(quote.gst)}</span></div>
              <div className="flex justify-between"><span className="flex items-center gap-1"><Truck className="h-3.5 w-3.5" />Delivery</span><span>{money(quote.delivery)}</span></div>
              <div className="flex justify-between font-black text-base pt-1 border-t"><span>Total</span><span className="text-[#1E3A8A]">{money(quote.total)}</span></div>
            </div>
          )}

          <div className="space-y-2 mt-3">
            <Input placeholder="Full name" value={info.full_name} onChange={e => setInfo({ ...info, full_name: e.target.value })} data-testid="paylink-name" />
            <Input placeholder="Phone (10-digit)" value={info.phone} onChange={e => setInfo({ ...info, phone: e.target.value })} data-testid="paylink-phone" />
            <Input placeholder="Delivery address" value={info.address} onChange={e => setInfo({ ...info, address: e.target.value })} data-testid="paylink-address" />
          </div>

          {cartCount < minItems && (
            <div className="text-xs font-semibold text-[var(--bz-orange)] bg-[var(--bz-orange-tint)] rounded-lg px-3 py-2 mt-3" data-testid="paylink-min-notice">
              Minimum {minItems} items — add {minItems - cartCount} more to continue.
            </div>
          )}

          <div className="mt-3">
            <div className="text-[11px] font-semibold text-slate-500 mb-1.5">Pay with</div>
            <div className="grid grid-cols-2 gap-2" data-testid="paylink-provider-selector">
              {[['razorpay', 'Razorpay'], ['cashfree', 'Cashfree']].map(([val, label]) => (
                <button key={val} type="button" onClick={() => setProvider(val)} data-testid={`paylink-provider-${val}`}
                  className={`rounded-xl border-2 py-2 text-sm font-bold transition-all ${provider === val ? 'border-[#1D4ED8] bg-[var(--bz-soft)] text-[#1E3A8A]' : 'border-slate-200 text-slate-500'}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          <Button onClick={pay} disabled={paying || cartCount < minItems || !info.full_name || !info.phone || !info.address} className="w-full rounded-full bg-[#1D4ED8] hover:bg-[#1E3A8A] mt-3" data-testid="paylink-pay-btn">
            {paying ? 'Processing…' : cartCount < minItems ? `Add ${minItems - cartCount} more item(s)` : `Pay ${quote ? money(quote.total) : ''} with ${provider === 'cashfree' ? 'Cashfree' : 'Razorpay'}`}
          </Button>
          <p className="text-[11px] text-slate-400 mt-2 flex items-center gap-1 justify-center"><ShieldCheck className="h-3.5 w-3.5" />Secure payment · no account needed</p>
        </Card>
      </div>
    </main>
  );
}
